import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { RootState } from '../../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import { fetchTeamList } from '../../../../states/containers/team/listSlice'
import { cloneDeep, orderBy } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmationModal from '../../../components/setting/admin/ConfirmationModal'
import EditModal from '../../../components/setting/admin/EditModal'
import { useAlert } from 'react-alert'
import MiniMenu from '../../../components/setting/admin/MiniMenu'
import { ReactSVG } from 'react-svg'
import { delAdministrator, fetchAdministratorList, updateAdministrator } from '../../../../states/containers/setting/systemSlice'
import VideoGuide from '../../../components/common/VideoGuide'
import { compile } from '../../../../utils/search'

interface MiniMenuState {
  display: boolean,
  x: number,
  y: number,
  id: number | undefined,
  status: string,
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

interface EditModalState {
  display: boolean,
  type: string,
}

interface AuthorityMember {
  id: number
  name: string
  email: string
  role: string
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    system,
    login,
  } = useSelector((state: RootState) => (state))

  const [miniMenuState, setMiniMenuState] = useState<MiniMenuState>({
    display: false,
    x: 0,
    y: 0,
    id: undefined,
    status: "",
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [editModalState, setEditModalState] = useState<EditModalState>({
    display: false,
    type: "",
  })

  const [memberList, setMemberList] = useState<AuthorityMember[]>()
  const [memberListInit, setMemberListInit] = useState<AuthorityMember[]>()
  const [searchWord, setSearchWord] = useState<string>("")

  const closeMiniMenu = () => {
    setMiniMenuState({
      ...miniMenuState,
      display: false,
    })
  }

  const deleteAdmin = (memberName: string) => {
    dispatch(
      delAdministrator(
        {
          member_id: Number(miniMenuState.id),
        },
        () => {
          dispatch(fetchAdministratorList())
          alert.success(t('setting.admin.message.delete', { name: memberName }))
        },
      ),
    )
    closeMiniMenu()
  }

  const delegationAdmin = (memberName: string) => {
    dispatch(
      updateAdministrator(
        {
          member_id: Number(miniMenuState.id),
        },
        () => {
          dispatch(fetchAdministratorList())
          alert.success(t('setting.admin.message.delegation', { name: memberName }))
        },
      ),
    )
    closeMiniMenu()
  }

  // confirmation modal
  const openConfirmationModal = (type: string) => {
    const memberData = memberList?.find((member) => member.id === miniMenuState.id)
    if (!memberData) return
    let callback = () => {
      // none
    }
    switch (type) {
      case "delegation":
        callback = () => delegationAdmin(memberData.name)
        break
      case "delete":
        callback = () => deleteAdmin(memberData.name)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: memberData.name,
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openEditModal = (type: string) => {
    setEditModalState({
      ...editModalState,
      display: true,
      type: type,
    })
  }

  const closeEditModal = () => {
    setEditModalState({
      ...editModalState,
      display: false,
    })
  }

  const openMiniMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const id = Number(event.currentTarget.dataset.id)
    const status = event.currentTarget.dataset.status as string
    const pageX = event.clientX + (window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft) - 50
    const pageY = event.clientY + (window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) + 20
    setMiniMenuState({
      ...miniMenuState,
      display: true,
      x: pageX,
      y: pageY,
      id: id,
      status: status,
    })
  }

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setMemberList(cloneDeep(memberListInit))
      return
    }
    const word = words.split(/\s+/)
    const list = memberListInit?.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name + member.email))
    }) as AuthorityMember[]
    setMemberList(orderBy(list, ['role', 'id'], ['desc', 'asc']))
  }

  const reset = () => {
    setSearchWord("")
    setMemberList(cloneDeep(memberListInit))
  }

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    dispatch(
      fetchTeamList(() => { /* do nothing */ }),
    )
    dispatch(fetchAdministratorList())
  }, [login.accessToken, commonPart.company])

  useEffect(() => {
    if (!system.administrator_list) return
    setMemberListInit(orderBy(system.administrator_list, ['role', 'id'], ['desc', 'asc']))
    setMemberList(orderBy(system.administrator_list, ['role', 'id'], ['desc', 'asc']))
  }, [system.administrator_list])

  useEffect(() => {
    if (!miniMenuState.display) return
    closeMiniMenu()
  }, [editModalState.display, confirmationModalState.display])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t('setting.admin.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.admin.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.admin.title')}
          <VideoGuide
            videoId="QeJkjplqCsQ"
          />
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder={t('member.list.search.placeholder')}
              onChange={(event) => search(event.target.value)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          管理者権限を付与、削除をすることができます。
        </p>
        <p>
          付与されたユーザーは共通設定の設定、組織分析の参照、工数管理各種の設定やプロジェクト設定権限等が付与されます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div />
            <div className="button-area">
              <button
                type="button"
                className="button"
                onClick={() => openEditModal('create')}
              >
                {t('setting.admin.add')}
              </button>
            </div>
          </div>
          <div className="block-detail">
            <table className="flex-table admin_list_table">
              <thead>
                <tr>
                  <th>{t('setting.admin.table.name')}</th>
                  <th>{t('setting.admin.table.email')}</th>
                  <th>{t('setting.admin.table.authority')}</th>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <th />
                </tr>
              </thead>
              <tbody>
                {
                  memberList && memberList.map((member) => (
                    <tr key={member.id}>
                      <td>{member.name}</td>
                      <td>{member.email}</td>
                      <td>
                        { t(`setting.admin.table.${member.role}`) }
                      </td>
                      <td>
                        {
                          memberList.length !== 1 && member.role !== 'master' && member.id !== commonPart.current_member?.id && (
                            <button
                              type="button"
                              className="mini_menu_button"
                              data-id={member.id}
                              onClick={(event) => openMiniMenu(event)}
                            >
                              <ReactSVG
                                src="/static/images/icon/icon-leader.svg"
                                className="icon"
                              />
                            </button>
                          )
                        }
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* メニュー */}
      <MiniMenu
        miniMenuState={miniMenuState}
        closeCallback={closeMiniMenu}
        deleteCallback={() => openConfirmationModal("delete")}
        delegationCallback={() => openConfirmationModal("delegation")}
      />

      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />

      {/* 編集モーダル */}
      <EditModal
        editModalState={editModalState}
        closeCallback={closeEditModal}
        closeable
      />
    </section>
  )
}

export default Index
